import React from 'react';
import { Clock, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

/**
 * TrialBanner - Shows trial status to org admins
 * Displays days remaining and upgrade CTA
 */
export const TrialBanner = ({ organization }) => {
  const navigate = useNavigate();
  
  if (!organization) return null;
  
  const {
    subscription_status,
    subscription_tier,
    trial_tier,
    trial_end,
    trial_start
  } = organization;
  
  // Only show for trial users
  if (subscription_status !== 'trial' && subscription_status !== 'trial_pending') {
    return null;
  }
  
  // Calculate days remaining
  const now = new Date();
  const endDate = new Date(trial_end);
  const daysRemaining = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
  
  // Determine urgency level
  const isUrgent = daysRemaining <= 3;
  const isExpired = daysRemaining === 0;
  
  if (isExpired) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <Clock className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-red-900">Your Premium Trial Has Ended</h3>
              <p className="text-sm text-red-700">
                Upgrade now to keep all premium features
              </p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/org-admin/settings/subscription')}
            className="bg-red-600 hover:bg-red-700"
          >
            Upgrade Now
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`rounded-lg p-4 mb-6 ${
      isUrgent 
        ? 'bg-amber-50 border border-amber-200' 
        : 'bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200'
    }`}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${isUrgent ? 'bg-amber-100' : 'bg-indigo-100'}`}>
            <Sparkles className={`h-5 w-5 ${isUrgent ? 'text-amber-600' : 'text-indigo-600'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`font-semibold ${isUrgent ? 'text-amber-900' : 'text-indigo-900'}`}>
                Premium Trial
              </h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isUrgent 
                  ? 'bg-amber-200 text-amber-800' 
                  : 'bg-indigo-200 text-indigo-800'
              }`}>
                {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
              </span>
            </div>
            <p className={`text-sm ${isUrgent ? 'text-amber-700' : 'text-indigo-700'}`}>
              {isUrgent 
                ? "Your trial ends soon! Upgrade to keep all features."
                : "You're enjoying full access to all premium features."
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-slate-500">Trial ends</p>
            <p className={`text-sm font-medium ${isUrgent ? 'text-amber-800' : 'text-slate-700'}`}>
              {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <Button 
            onClick={() => navigate('/org-admin/settings/subscription')}
            variant={isUrgent ? "default" : "outline"}
            className={isUrgent ? "bg-amber-600 hover:bg-amber-700" : "border-indigo-300 text-indigo-700 hover:bg-indigo-50"}
          >
            {isUrgent ? "Upgrade Now" : "View Plans"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Feature highlights */}
      <div className="mt-3 pt-3 border-t border-indigo-100/50">
        <p className="text-xs text-slate-500 mb-2">Premium features you're enjoying:</p>
        <div className="flex flex-wrap gap-2">
          {['Unlimited Programs', 'AI Features', 'Email Notifications', 'Custom Branding', 'Lower Fees (3%)'].map((feature) => (
            <span 
              key={feature}
              className="text-xs px-2 py-1 bg-white/70 rounded-full text-slate-600 border border-slate-200"
            >
              ✓ {feature}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
